import type {
  CreateRequisitionInput,
  RequisitionDetail,
  RequisitionStatus,
  RequisitionSuggestion,
} from '#shared/types';

/** One reagent on an order being drafted. `item_id` is 0 until one is chosen. */
export interface DraftRequisitionLine {
  item_id: number;
  quantity_packs: number;
  unit_price: number | null;
  note: string | null;
}

export type RequisitionFields = Omit<CreateRequisitionInput, 'lines'>;

interface NamedRecord {
  id: number;
  name: string;
}

interface ReagentChoice extends NamedRecord {
  pack_label: string;
}

/** A line with nothing chosen yet. Module scope: `useRepeater` copies it per row. */
export const EMPTY_REQUISITION_LINE: DraftRequisitionLine = {
  item_id: 0,
  quantity_packs: 1,
  unit_price: null,
  note: null,
};

export function initialRequisitionLines(
  record: RequisitionDetail | undefined,
): DraftRequisitionLine[] {
  return (
    record?.lines.map((line) => ({
      item_id: line.item_id,
      quantity_packs: line.quantity_packs,
      unit_price: line.unit_price,
      note: line.note,
    })) ?? []
  );
}

export function initialRequisitionFields(record: RequisitionDetail | undefined): RequisitionFields {
  return {
    supplier_id: record?.supplier_id ?? null,
    department_id: record?.department_id ?? null,
    expected_at: record?.expected_at?.slice(0, 10) ?? '',
    note: record?.note ?? '',
  };
}

/** The reorder-list preview as draft lines, noting why each one is there. */
export function linesFromSuggestions(
  suggestions: readonly RequisitionSuggestion[],
): DraftRequisitionLine[] {
  return suggestions.map((suggestion) => ({
    item_id: suggestion.item_id,
    quantity_packs: suggestion.quantity_packs,
    unit_price: null,
    note: `On hand ${suggestion.on_hand_base}, reorder at ${suggestion.reorder_level_base}`,
  }));
}

export function describeSuggestions(count: number): string {
  return count === 0
    ? 'Nothing is at or below its reorder level.'
    : `${count} ${count === 1 ? 'reagent is' : 'reagents are'} at or below the reorder level. Check the quantities before sending.`;
}

/**
 * The reagents one line may choose from.
 *
 * A reagent already on the order is hidden rather than marked: a second line
 * for the same item is a quantity change, and the server refuses it outright.
 */
export function reagentOptionsFor(
  reagents: readonly ReagentChoice[],
  currentItemId: number,
  chosenItemIds: ReadonlySet<number>,
) {
  return reagents
    .filter((reagent) => reagent.id === currentItemId || !chosenItemIds.has(reagent.id))
    .map((reagent) => ({
      value: String(reagent.id),
      label: `${reagent.name} — ${reagent.pack_label}`,
    }));
}

/** What the order comes to at the quoted prices; unquoted lines count as nothing. */
export function calculateOrderValue(
  lines: readonly { quantity_packs: number; unit_price: number | null }[],
): number {
  return lines.reduce((sum, line) => sum + line.quantity_packs * (line.unit_price ?? 0), 0);
}

/** Mirrors `canReceive` on the server. */
export function canReceive(status: RequisitionStatus): boolean {
  return status === 'approved' || status === 'ordered' || status === 'partially_received';
}
